import { lazy } from "react";
import { BASE_PATH } from "context/SiteSettingsContext/utils";

export interface RouteMapping {
  id: string;
  path: string;
  components: Record<string, React.LazyExoticComponent<React.ComponentType>>;
}

export interface IRedirectMapping {
  from: string;
  to: string;
}

// Route mapping keys - ordered by priority
export enum ERouteMappingKeys {
  CLOUD_GATEWAYS = "CLOUD_GATEWAYS",
  SWITCHING = "SWITCHING",
  WIFI = "WIFI",
  DOOR_ACCESS = "DOOR_ACCESS",
  ACCESSORY_TECH = "ACCESSORY_TECH",
  WHATS_NEW = "WHATS_NEW",
  MANAGED_VOIP = "MANAGED_VOIP",
  SPECIAL_DEVICES = "SPECIAL_DEVICES",
  HOW_IT_WORKS = "HOW_IT_WORKS",
  PREMIUM_IOT = "PREMIUM_IOT",
  POWER_TECH = "POWER_TECH",
  IDENTITY = "IDENTITY",
  PHYSICAL_SECURITY = "PHYSICAL_SECURITY",
  MOBILE_ROUTING = "MOBILE_ROUTING",
  NETWORK_STORAGE = "NETWORK_STORAGE",
  CLOUD_KEYS_GATEWAYS = "CLOUD_KEYS_GATEWAYS",
  DOWNLOAD_APP = "DOWNLOAD_APP",
  DOWNLOAD = "DOWNLOAD",
  PARTNER_HUB = "PARTNER_HUB",
  UI_CARE = "UI_CARE",
  ULTRA = "ULTRA",
  VIDEO_RECORDERS = "VIDEO_RECORDERS",
  PROFESSIONAL_INTEGRATORS = "PROFESSIONAL_INTEGRATORS",
  SITE_SUPPORT = "SITE_SUPPORT",
  TRUST_CENTER = "TRUST_CENTER",
  DOWNLOAD_SOFTWARE = "DOWNLOAD_SOFTWARE",
  DOWNLOAD_RELEASES = "DOWNLOAD_RELEASES",
  DOWNLOAD_PRODUCT_GROUP = "DOWNLOAD_PRODUCT_GROUP",
}

/**
 * Route mapping object - defines all route groups and their lazy-loaded components
 * Routes are ordered by priority - most frequently accessed routes come first
 * for optimal React Router matching performance.
 * A new route will need a base path and a component.
 * An existing route can have as many sub-paths as needed.
 */
export const ROUTE_MAP: Record<ERouteMappingKeys, RouteMapping> = {
  [ERouteMappingKeys.CLOUD_GATEWAYS]: {
    id: "cloud-gateways",
    path: "/cloud-gateways",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/CloudGateways/Overview").then((p) => ({
          default: p.CloudGatewaysOverview,
        })),
      ),
      "resource-calculator": lazy(() =>
        import("pages/Sections/CloudGateways/ResourceCalculator").then((p) => ({
          default: p.ResourceCalculator,
        })),
      ),
      "enterprise-scale": lazy(() =>
        import("pages/Sections/CloudGateways/EnterpriseScale").then((p) => ({
          default: p.EnterpriseScale,
        })),
      ),
      "large-scale": lazy(() =>
        import("pages/Sections/CloudGateways/LargeScale").then((p) => ({
          default: p.LargeScale,
        })),
      ),
      "wifi-integrated": lazy(() =>
        import("pages/Sections/CloudGateways/WiFiIntegrated").then((p) => ({
          default: p.WiFiIntegrated,
        })),
      ),
      compact: lazy(() =>
        import("pages/Sections/CloudGateways/Compact").then((p) => ({
          default: p.Compact,
        })),
      ),
    },
  },
  [ERouteMappingKeys.SWITCHING]: {
    id: "switching",
    path: "/switching",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/Switching/Overview").then((p) => ({
          default: p.Overview,
        })),
      ),
      enterprise: lazy(() =>
        import("pages/Sections/Switching/Enterprise").then((p) => ({
          default: p.Enterprise,
        })),
      ),
      professional: lazy(() =>
        import("pages/Sections/Switching/Professional").then((p) => ({
          default: p.Professional,
        })),
      ),
      value: lazy(() =>
        import("pages/Sections/Switching/Standard").then((p) => ({
          default: p.Standard,
        })),
      ),
      compact: lazy(() =>
        import("pages/Sections/Switching/Compact").then((p) => ({
          default: p.Compact,
        })),
      ),
      wan: lazy(() =>
        import("pages/Sections/Switching/Wan").then((p) => ({
          default: p.Wan,
        })),
      ),
    },
  },
  [ERouteMappingKeys.WIFI]: {
    id: "wifi",
    path: "/wifi",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/WiFi/Overview").then((p) => ({
          default: p.Overview,
        })),
      ),
      flagship: lazy(() =>
        import("pages/Sections/WiFi/Flagship").then((p) => ({
          default: p.Flagship,
        })),
      ),
      enterprise: lazy(() =>
        import("pages/Sections/WiFi/Enterprise").then((p) => ({
          default: p.WiFiEnterprise,
        })),
      ),
      wall: lazy(() =>
        import("pages/Sections/WiFi/Wall").then((p) => ({
          default: p.Wall,
        })),
      ),
      outdoor: lazy(() =>
        import("pages/Sections/WiFi/Outdoor").then((p) => ({
          default: p.Outdoor,
        })),
      ),
      bridging: lazy(() =>
        import("pages/Sections/WiFi/Bridging/Bridging").then((p) => ({
          default: p.Bridging,
        })),
      ),
    },
  },
  [ERouteMappingKeys.DOOR_ACCESS]: {
    id: "door-access",
    path: "/physical-security/door-access",
    components: {
      [BASE_PATH]: lazy(() =>
        import(
          "pages/Sections/CameraSecurity/DoorAccess/ReadersHubsStarterKits"
        ).then((p) => ({ default: p.ReadersHubsStarterKits })),
      ),
      intercoms: lazy(() =>
        import("pages/Sections/CameraSecurity/DoorAccess/Intercoms").then(
          (p) => ({ default: p.Intercoms }),
        ),
      ),
      "touch-pass": lazy(() =>
        import("pages/Sections/CameraSecurity/DoorAccess/TouchPass").then(
          (p) => ({ default: p.TouchPass }),
        ),
      ),
    },
  },
  [ERouteMappingKeys.ACCESSORY_TECH]: {
    id: "accessory-tech",
    path: "/integrations/accessory-tech",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/Accessories").then((p) => ({
          default: p.Accessories,
        })),
      ),
      "internet-solutions": lazy(() =>
        import("pages/Sections/Integrations/InternetSolutions").then((p) => ({
          default: p.InternetSolutions,
        })),
      ),
      "sfp-wizard": lazy(() =>
        import("pages/Sections/Integrations/SfpWizard/SfpWizard").then((p) => ({
          default: p.SfpWizard,
        })),
      ),
    },
  },
  [ERouteMappingKeys.WHATS_NEW]: {
    id: "whats-new",
    path: "/whats-new",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/WhatsNew").then((p) => ({
          default: p.WhatsNew,
        })),
      ),
    },
  },
  [ERouteMappingKeys.MANAGED_VOIP]: {
    id: "managed-voip",
    path: "/integrations/managed-voip",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/Integrations/ManagedVoIP/ManagedVoIP").then(
          (p) => ({ default: p.ManagedVoIP }),
        ),
      ),
    },
  },
  [ERouteMappingKeys.SPECIAL_DEVICES]: {
    id: "special-devices",
    path: "/physical-security/special-devices",
    components: {
      "compact-cameras": lazy(() =>
        import("pages/Sections/CameraSecurity/SpecialDevices").then((p) => ({
          default: p.SpecialDevices,
        })),
      ),
      "super-link": lazy(() =>
        import("pages/Sections/CameraSecurity/SpecialDevices/SuperLink").then(
          (p) => ({ default: p.SuperLink }),
        ),
      ),
      "ai-enhancements": lazy(() =>
        import(
          "pages/Sections/CameraSecurity/SpecialDevices/AIEnhancements"
        ).then((p) => ({ default: p.AIEnhancements })),
      ),
      floodlight: lazy(() =>
        import("pages/Sections/CameraSecurity/SpecialDevices/Floodlight").then(
          (p) => ({ default: p.Floodlight }),
        ),
      ),
      doorbells: lazy(() =>
        import("pages/Sections/CameraSecurity/SpecialDevices/Doorbells").then(
          (p) => ({ default: p.Doorbells }),
        ),
      ),
    },
  },
  [ERouteMappingKeys.HOW_IT_WORKS]: {
    id: "how-it-works",
    path: "/how-it-works",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/GettingStartedWithUniFi").then((p) => ({
          default: p.GettingStartedWithUniFi,
        })),
      ),
      "multi-site": lazy(() =>
        import("pages/Sections/GettingStartedWithUniFi/MultiSite").then(
          (p) => ({ default: p.MultiSiteDeployment }),
        ),
      ),
      "massive-scale": lazy(() =>
        import("pages/Sections/GettingStartedWithUniFi/MassiveScale").then(
          (p) => ({ default: p.MassiveScaleDeployment }),
        ),
      ),
      large: lazy(() =>
        import("pages/Sections/GettingStartedWithUniFi/RackMounted").then(
          (p) => ({ default: p.RackMountedDeployment }),
        ),
      ),
    },
  },
  [ERouteMappingKeys.PREMIUM_IOT]: {
    id: "premium-iot",
    path: "/integrations/premium-iot",
    components: {
      "digital-signage": lazy(() =>
        import("pages/Sections/Integrations/DigitalSignage").then((p) => ({
          default: p.DigitalSignage,
        })),
      ),
      "ev-charging": lazy(() =>
        import("pages/Sections/Integrations/EVCharging").then((p) => ({
          default: p.EVCharging,
        })),
      ),
      "premium-audio": lazy(() =>
        import("pages/Sections/Integrations/PremiumAudio").then((p) => ({
          default: p.PremiumAudio,
        })),
      ),
    },
  },
  [ERouteMappingKeys.POWER_TECH]: {
    id: "power-tech",
    path: "/integrations/power-tech",
    components: {
      "ups-solutions": lazy(() =>
        import("pages/Sections/Integrations/UPSSolutions").then((p) => ({
          default: p.UPSSolutions,
        })),
      ),
      "power-solutions": lazy(() =>
        import("pages/Sections/Integrations/PowerSolutions").then((p) => ({
          default: p.PowerSolutions,
        })),
      ),
    },
  },
  [ERouteMappingKeys.IDENTITY]: {
    id: "identity",
    path: "/identity",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/Identity/Identity").then((p) => ({
          default: p.Identity,
        })),
      ),
      /**
       * Special case/request for the desktop QR code pairing page
       * @see https://ui.slack.com/archives/GMDLV5QPN/p1747722286236019?thread_ts=1742182262.626689&cid=GMDLV5QPN
       */
      "qrcode/desktop/bt_pairing": lazy(() =>
        import("pages/Sections/Identity/Identity").then((p) => ({
          default: p.Identity,
        })),
      ),
    },
  },
  [ERouteMappingKeys.PHYSICAL_SECURITY]: {
    id: "physical-security",
    path: "/physical-security",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/CameraSecurity/Overview").then((p) => ({
          default: p.Overview,
        })),
      ),
      bullet: lazy(() =>
        import("pages/Sections/CameraSecurity/Bullet").then((p) => ({
          default: p.Bullet,
        })),
      ),
      "dome-turret": lazy(() =>
        import("pages/Sections/CameraSecurity/DomeTurret").then((p) => ({
          default: p.DomeTurret,
        })),
      ),
      ptz: lazy(() =>
        import("pages/Sections/CameraSecurity/Ptz").then((p) => ({
          default: p.Ptz,
        })),
      ),
    },
  },
  [ERouteMappingKeys.MOBILE_ROUTING]: {
    id: "mobile-routing",
    path: "/integrations/mobile-routing",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/Integrations/MobileRouting").then((p) => ({
          default: p.MobileRouting,
        })),
      ),
    },
  },
  [ERouteMappingKeys.NETWORK_STORAGE]: {
    id: "network-storage",
    path: "/integrations/network-storage",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/Integrations/NetworkStorage").then((p) => ({
          default: p.NetworkStorage,
        })),
      ),
    },
  },
  [ERouteMappingKeys.CLOUD_KEYS_GATEWAYS]: {
    id: "cloud-keys-gateways",
    path: "/integrations/cloud-keys-gateways",
    components: {
      "control-plane-options": lazy(() =>
        import("pages/Sections/Integrations/ControlPlaneOptions").then((p) => ({
          default: p.ControlPlaneOptions,
        })),
      ),
      "independent-gateways": lazy(() =>
        import("pages/Sections/Integrations/IndependentGateways").then((p) => ({
          default: p.IndependentGateways,
        })),
      ),
    },
  },
  [ERouteMappingKeys.DOWNLOAD_APP]: {
    id: "download-app",
    path: "/download/app",
    components: {
      "wifiman-desktop": lazy(
        () => import("pages/Sections/Download/Wifiman"),
      ),
      "identity-desktop": lazy(
        () => import("pages/Sections/Download/Identity"),
      ),
      "identity-ios": lazy(
        () => import("pages/Sections/Download/Identity/Mobile/Apple"),
      ),
      "identity-android": lazy(
        () => import("pages/Sections/Download/Identity/Mobile/Android"),
      ),
    },
  },
  [ERouteMappingKeys.DOWNLOAD_SOFTWARE]: {
    id: "download-software",
    path: `/download/software/:slug`,
    components: {
      [BASE_PATH]: lazy(
        () => import("../../pages/Sections/Download/ProductDownloadsPage"),
      ),
    },
  },
  [ERouteMappingKeys.DOWNLOAD_RELEASES]: {
    id: "download-releases",
    path: `/download/releases/:slug`,
    components: {
      [BASE_PATH]: lazy(
        () => import("pages/Sections/Download/PastReleasesPage"),
      ),
    },
  },
  [ERouteMappingKeys.DOWNLOAD]: {
    id: "download",
    path: "/download",
    components: {
      [BASE_PATH]: lazy(() => import("pages/Sections/Download/LandingPage")),
      "unifi/download-started": lazy(() =>
        import("pages/Sections/DownloadStarted/DownloadStarted").then((p) => ({
          default: p.DownloadStarted,
        })),
      ),
    },
  },
  [ERouteMappingKeys.PARTNER_HUB]: {
    id: "partner-hub",
    path: "/partner-hub",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/UniFiPartnerHub/UniFiPartnerHub").then((p) => ({
          default: p.UniFiPartnerHub,
        })),
      ),
    },
  },
  [ERouteMappingKeys.UI_CARE]: {
    id: "ui-care",
    path: "/ui-care",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/UICare").then((p) => ({
          default: p.UICare,
        })),
      ),
    },
  },
  [ERouteMappingKeys.ULTRA]: {
    id: "ultra",
    path: "/ultra",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/Ultra").then((p) => ({
          default: p.Ultra,
        })),
      ),
    },
  },
  [ERouteMappingKeys.VIDEO_RECORDERS]: {
    id: "video-recorders",
    path: "/physical-security/video-recorders",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/CameraSecurity/VideoRecorders").then((p) => ({
          default: p.VideoRecorders,
        })),
      ),
    },
  },
  [ERouteMappingKeys.PROFESSIONAL_INTEGRATORS]: {
    id: "professional-integrators",
    path: "/professional-integrators",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/ProfessionalIntegrators").then((p) => ({
          default: p.ProfessionalIntegrators,
        })),
      ),
    },
  },
  [ERouteMappingKeys.SITE_SUPPORT]: {
    id: "site-support",
    path: "/site-support",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/SiteSupport").then((p) => ({
          default: p.SiteSupport,
        })),
      ),
    },
  },
  [ERouteMappingKeys.TRUST_CENTER]: {
    id: "trust-center",
    path: "/trust-center",
    components: {
      [BASE_PATH]: lazy(() =>
        import("pages/Sections/TrustCenter").then((p) => ({
          default: p.TrustCenter,
        })),
      ),
    },
  },
  [ERouteMappingKeys.DOWNLOAD_PRODUCT_GROUP]: {
    id: "download-product-group",
    path: `/download/:productGroupId`,
    components: {
      [BASE_PATH]: lazy(
        () => import("pages/Sections/Download/ProductGroupDownloadsPage"),
      ),
    },
  },
};

// Redirect mappings (internal and external)
export const REDIRECT_MAP: IRedirectMapping[] = [
  // Console redirects
  { from: "/consoles", to: "/cloud-gateways" },
  {
    from: "/consoles/resource-calculator",
    to: "/cloud-gateways/resource-calculator",
  },

  // Door access redirects
  { from: "/door-access", to: "/physical-security/door-access" },
  { from: "/door-access/control-hubs", to: "/physical-security/door-access" },
  { from: "/door-access/readers", to: "/physical-security/door-access" },
  {
    from: "/door-access/intercoms",
    to: "/physical-security/door-access/intercoms",
  },
  {
    from: "/door-access/locks-accessories",
    to: "/physical-security/door-access/touch-pass",
  },

  // Special Devices redirects
  {
    from: "/physical-security/special-devices",
    to: "/physical-security/special-devices/compact-cameras",
  },

  // Integrations redirects
  { from: "/phone-system", to: "/integrations/managed-voip" },
  { from: "/integrations", to: "/integrations/managed-voip" },
  {
    from: "/integrations/premium-iot",
    to: "/integrations/premium-iot/ev-charging",
  },
  {
    from: "/integrations/cloud-keys-gateways",
    to: "/integrations/cloud-keys-gateways/control-plane-options",
  },
  {
    from: "/integrations/power-tech",
    to: "/integrations/power-tech/ups-solutions",
  },

  // Accessory redirects
  { from: "/unifi-accessories", to: "/integrations/accessory-tech" },
  { from: "/accessory-tech", to: "/integrations/accessory-tech" },
  {
    from: "/accessory-tech/cloud-keys-gateways",
    to: "/integrations/cloud-keys-gateways/control-plane-options",
  },

  // Download redirects
  {
    from: "/download/unifi/unifi-os-native/download-started",
    to: "/download/unifi/download-started",
  },
  {
    from: "/download/unifi/unifi-os/download-started",
    to: "/download/unifi/download-started",
  },

  // External redirects (UISP)
  { from: "/uisp", to: "https://uisp.com" },
  {
    from: "/uisp/ptp-bridging",
    to: "https://uisp.com/wireless/airfiber-and-ltu/airfiber",
  },
  {
    from: "/uisp/multipoint",
    to: "https://uisp.com/wireless/airfiber-and-ltu/ltu",
  },
  { from: "/uisp/wired-technologies", to: "https://uisp.com/wired" },
  { from: "/uisp/accessories", to: "https://uisp.com/accessories" },
  { from: "/uisp/*", to: "https://uisp.ui.com" },
];
